import React, { useState, useEffect } from 'react';
import abTestService, { ABTest, CreateABTestData, ABTestResults } from '../../../services/abTestService';
import {
  BeakerIcon,
  ArrowPathIcon,
  PlusIcon,
  PlayIcon,
  PauseIcon,
  CheckCircleIcon,
  TrashIcon,
  ChartBarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const ABTestingPage: React.FC = () => {
  const [tests, setTests] = useState<ABTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<ABTest | null>(null);
  const [testResults, setTestResults] = useState<ABTestResults | null>(null);
  const [formData, setFormData] = useState<CreateABTestData>({
    name: '',
    description: '',
    type: 'TIER_PRICE',
    variantA: {},
    variantB: {},
    conversionMetric: 'patron_count',
    minSampleSize: 100,
  });

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      setLoading(true);
      const data = await abTestService.getAll();
      setTests(data);
    } catch (error) {
      console.error('Failed to load A/B tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedTest) {
        // Edit not implemented in service, would need update method
        console.log('Edit test:', selectedTest.id, formData);
      } else {
        await abTestService.create(formData);
      }
      setShowCreateModal(false);
      resetForm();
      loadTests();
    } catch (error) {
      console.error('Failed to save A/B test:', error);
      alert('Failed to save test. Please try again.');
    }
  };

  const handleStart = async (testId: string) => {
    if (confirm('Are you sure you want to start this test?')) {
      try {
        await abTestService.start(testId);
        loadTests();
      } catch (error) {
        console.error('Failed to start test:', error);
        alert('Failed to start test.');
      }
    }
  };

  const handlePause = async (testId: string) => {
    if (confirm('Are you sure you want to pause this test?')) {
      try {
        await abTestService.pause(testId);
        loadTests();
      } catch (error) {
        console.error('Failed to pause test:', error);
        alert('Failed to pause test.');
      }
    }
  };

  const handleResume = async (testId: string) => {
    if (confirm('Resume this test?')) {
      try {
        await abTestService.resume(testId);
        loadTests();
      } catch (error) {
        console.error('Failed to resume test:', error);
        alert('Failed to resume test.');
      }
    }
  };

  const handleComplete = async (testId: string) => {
    if (confirm('Are you sure you want to complete this test? This will end the test and declare a winner.')) {
      try {
        await abTestService.complete(testId);
        loadTests();
      } catch (error) {
        console.error('Failed to complete test:', error);
        alert('Failed to complete test.');
      }
    }
  };

  const handleDelete = async (testId: string) => {
    if (confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      try {
        await abTestService.delete(testId);
        loadTests();
      } catch (error) {
        console.error('Failed to delete test:', error);
        alert('Failed to delete test.');
      }
    }
  };

  const handleViewResults = async (test: ABTest) => {
    try {
      const results = await abTestService.getResults(test.id);
      setTestResults(results);
      setSelectedTest(test);
      setShowResultsModal(true);
    } catch (error) {
      console.error('Failed to load results:', error);
      alert('Failed to load test results.');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setSelectedTest(null);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'TIER_PRICE',
      variantA: {},
      variantB: {},
      conversionMetric: 'patron_count',
      minSampleSize: 100,
    });
    setSelectedTest(null);
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string }> = {
      DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800' },
      RUNNING: { bg: 'bg-blue-100', text: 'text-blue-800' },
      PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-800' },
    };
    const badge = badges[status] || badges.DRAFT;
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${badge.bg} ${badge.text}`}>
        {status}
      </span>
    );
  };

  const getTestTypeName = (type: string) => {
    const types: Record<string, string> = {
      TIER_PRICE: 'Tier Price',
      TIER_NAME: 'Tier Name',
      POST_TITLE: 'Post Title',
      CUSTOM: 'Custom',
    };
    return types[type] || type;
  };

  const stats = {
    total: tests.length,
    running: tests.filter((t) => t.status === 'RUNNING').length,
    completed: tests.filter((t) => t.status === 'COMPLETED').length,
    drafts: tests.filter((t) => t.status === 'DRAFT').length,
    paused: tests.filter((t) => t.status === 'PAUSED').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BeakerIcon className="h-10 w-10 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">A/B Testing Dashboard</h1>
                <p className="text-gray-600 mt-1">Experiment and optimize your creator page</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadTests}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Test
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tests</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <BeakerIcon className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Running</p>
                <p className="text-2xl font-bold text-blue-600">{stats.running}</p>
              </div>
              <PlayIcon className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-600">{stats.drafts}</p>
              </div>
              <PauseIcon className="h-8 w-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Tests List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-600">Loading tests...</span>
          </div>
        ) : tests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No A/B tests</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new test</p>
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Test
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {tests.map((test) => (
              <div key={test.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{test.name}</h3>
                      {getStatusBadge(test.status)}
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {getTestTypeName(test.type)}
                      </span>
                    </div>
                    {test.description && (
                      <p className="text-sm text-gray-600 mb-3">{test.description}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Min Sample Size</p>
                        <p className="font-medium text-gray-900">{test.minSampleSize || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Conversion Metric</p>
                        <p className="font-medium text-gray-900">{test.conversionMetric}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Created</p>
                        <p className="font-medium text-gray-900">
                          {new Date(test.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Status</p>
                        <p className="font-medium text-gray-900">{test.status}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {test.status === 'DRAFT' && (
                      <button
                        onClick={() => handleStart(test.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Start Test"
                      >
                        <PlayIcon className="h-5 w-5" />
                      </button>
                    )}
                    {test.status === 'RUNNING' && (
                      <>
                        <button
                          onClick={() => handlePause(test.id)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                          title="Pause Test"
                        >
                          <PauseIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleComplete(test.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Complete Test"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                    {test.status === 'PAUSED' && (
                      <>
                        <button
                          onClick={() => handleResume(test.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Resume Test"
                        >
                          <PlayIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleComplete(test.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Complete Test"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                    {(test.status === 'RUNNING' || test.status === 'PAUSED' || test.status === 'COMPLETED') && (
                      <button
                        onClick={() => handleViewResults(test)}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                        title="View Results"
                      >
                        <ChartBarIcon className="h-5 w-5" />
                      </button>
                    )}
                    {test.status === 'DRAFT' && (
                      <button
                        onClick={() => handleDelete(test.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete Test"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {selectedTest ? 'Edit Test' : 'Create New Test'}
                  </h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Test Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev: CreateABTestData) => ({ ...prev, name: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Tier Pricing Test"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev: CreateABTestData) => ({ ...prev, description: e.target.value }))
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="What are you testing?"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Type *
                      </label>
                      <select
                        required
                        value={formData.type}
                        onChange={(e) =>
                          setFormData((prev: CreateABTestData) => ({
                            ...prev,
                            type: e.target.value as CreateABTestData['type'],
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="TIER_PRICE">Tier Price</option>
                        <option value="TIER_NAME">Tier Name</option>
                        <option value="POST_TITLE">Post Title</option>
                        <option value="CUSTOM">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Sample Size *
                      </label>
                      <input
                        type="number"
                        required
                        min="10"
                        value={formData.minSampleSize}
                        onChange={(e) =>
                          setFormData((prev: CreateABTestData) => ({
                            ...prev,
                            minSampleSize: parseInt(e.target.value),
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Conversion Metric *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.conversionMetric}
                      onChange={(e) =>
                        setFormData((prev: CreateABTestData) => ({
                          ...prev,
                          conversionMetric: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., patron_count, revenue, click_through"
                    />
                  </div>
                  <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                    <p className="font-medium mb-1">Note:</p>
                    <p>
                      Variant A and B configuration will be determined by the test type selected.
                      After creating the test, you can configure specific values for each variant.
                    </p>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      {selectedTest ? 'Save Changes' : 'Create Test'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Results Modal */}
        {showResultsModal && testResults && selectedTest && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Test Results: {selectedTest.name}</h2>
                  <button
                    onClick={() => setShowResultsModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-6">
                  {/* Winner */}
                  {testResults.winner && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-lg font-semibold text-green-900">
                        🎉 Winner: Variant {testResults.winner}
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        Statistical significance: {testResults.statisticalSignificance ? 'Yes' : 'No'}
                        {testResults.confidence && ` (${testResults.confidence.toFixed(1)}% confidence)`}
                      </p>
                    </div>
                  )}

                  {/* Variant Comparison */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-blue-900 mb-3">Variant A</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-700">Views:</span>
                          <span className="font-semibold text-blue-900">{testResults.variantAData.views}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Conversions:</span>
                          <span className="font-semibold text-blue-900">
                            {testResults.variantAData.conversions}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-700">Conversion Rate:</span>
                          <span className="font-semibold text-blue-900">
                            {testResults.variantAData.conversionRate.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-purple-900 mb-3">Variant B</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-purple-700">Views:</span>
                          <span className="font-semibold text-purple-900">{testResults.variantBData.views}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-700">Conversions:</span>
                          <span className="font-semibold text-purple-900">
                            {testResults.variantBData.conversions}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-purple-700">Conversion Rate:</span>
                          <span className="font-semibold text-purple-900">
                            {testResults.variantBData.conversionRate.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Confidence Level */}
                  {testResults.confidence !== undefined && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Confidence Level</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {testResults.confidence.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            testResults.confidence >= 95
                              ? 'bg-green-600'
                              : testResults.confidence >= 80
                              ? 'bg-yellow-600'
                              : 'bg-red-600'
                          }`}
                          style={{ width: `${Math.min(testResults.confidence, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {testResults.confidence >= 95
                          ? 'High confidence - results are statistically significant'
                          : testResults.confidence >= 80
                          ? 'Moderate confidence - continue testing for better results'
                          : 'Low confidence - more data needed for conclusive results'}
                      </p>
                    </div>
                  )}

                  {/* Improvement */}
                  {testResults.improvement !== undefined && testResults.improvement !== 0 && (
                    <div className={`rounded-lg p-4 ${
                      testResults.improvement > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${
                          testResults.improvement > 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                          Performance Change
                        </span>
                        <span className={`text-lg font-bold ${
                          testResults.improvement > 0 ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {testResults.improvement > 0 ? '+' : ''}{testResults.improvement.toFixed(2)}%
                        </span>
                      </div>
                      <p className={`text-xs mt-1 ${
                        testResults.improvement > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Variant B shows a {Math.abs(testResults.improvement).toFixed(2)}% {
                          testResults.improvement > 0 ? 'improvement' : 'decrease'
                        } compared to Variant A
                      </p>
                    </div>
                  )}

                  {/* Recommended Action */}
                  {testResults.recommendedAction && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-1">Recommended Action</h4>
                      <p className="text-sm text-blue-700">{testResults.recommendedAction}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ABTestingPage;
